/**
 * The Diamond Sutra — editorial authority essays, tracks 1–6.
 * Thematic analysis only. No recording facts, credits, durations, lyrics,
 * or streaming destinations are asserted here.
 */
import type { SongEssay } from "./types";

export const DIAMOND_SUTRA_ESSAYS_1_6: Record<string, SongEssay> = {
  "collect-my-thoughts": {
    seoTitle: "Collect My Thoughts — Buddha KA | The Diamond Sutra",
    seoDescription:
      "The opening movement of The Diamond Sutra. Buddha KA gathers scattered attention into one place and draws the line between having thoughts and being governed by them. Released August 19, 2026.",
    standfirst:
      "Before anything can be examined, it has to be gathered. This is the intake point of the record.",
    sections: [
      {
        eyebrow: "About the song",
        title: "About the Song",
        paragraphs: [
          "“Collect My Thoughts” opens the album at the only honest place an inquiry can begin: with a mind that is not yet assembled. Most people never meet their own thinking directly. They meet the residue of it — the mood it leaves, the decision it forced, the argument it already won before they arrived.",
          "The song treats collection as a physical act. Attention has been left in a dozen rooms: unfinished conversations, notifications, obligations, old resentments still drawing interest. Gathering it back is not relaxation. It is retrieval, and retrieval costs something.",
          "As an opening statement it sets the terms for everything that follows on the record. The album's later chapters take apart perception, inherited belief, desire, language, and value. None of that dismantling is possible from a scattered position.",
        ],
      },
      {
        eyebrow: "The distinction",
        title: "Having Thoughts vs. Being Governed by Them",
        paragraphs: [
          "There is a gap between a thought occurring and a thought ruling. In a scattered state that gap closes to nothing. Whatever arrives loudest becomes the operating instruction, and the person experiences their own conditioning as personality.",
          "Collection widens the gap again. When attention is gathered, a thought can be held long enough to be inspected — where it came from, what it wants, what it costs to obey. That inspection is the entire difference between a mind that is used and a mind that is lived in.",
          "The song does not argue for emptiness. It argues for custody. A thought that has been noticed is a thought that can be declined.",
        ],
      },
      {
        eyebrow: "The method",
        title: "The Internal Mechanism",
        paragraphs: [
          "Scatter is not a moral failure; it is a design outcome. Modern attention is farmed. Every surface a person touches is engineered to take a fragment and keep it. What is called distraction is usually extraction.",
          "Understood that way, collection becomes a discipline of ownership rather than a productivity trick. The question shifts from “how do I focus?” to “where has my attention been deposited, and who is holding it?”",
          "The remedy the song points toward is unglamorous: name what is pulling, retrieve it deliberately, and refuse to begin any serious work from a borrowed state of mind.",
        ],
      },
      {
        eyebrow: "Context",
        title: "Inside The Diamond Sutra",
        paragraphs: [
          "The Diamond Sutra is a record about the structures operating underneath ordinary consciousness — how perception is trained, how belief is installed, how value is assigned. Track one is the doorway rather than the argument.",
          "Placed first, “Collect My Thoughts” frames the album as an examination the listener participates in rather than watches. The later interrogations of inherited seeing and inherited wanting only land on a mind that has been assembled enough to receive them.",
          "It also establishes the record's refusal of spectacle. The opening move is interior, quiet, and administrative — the work of a practitioner taking inventory before taking action.",
        ],
      },
      {
        eyebrow: "Listening",
        title: "What to Listen For",
        paragraphs: [
          "Listen as a participant. The song's subject is the listener's own attention, and the honest test is whether it can be held for the full length of the track without leaving.",
          "Notice where the mind departs. Whatever it leaves toward is a live piece of unfinished business, and the departure is more informative than the return.",
          "Then notice what remains once the gathering is done. What survives collection is usually much smaller — and far more usable — than the noise it was buried under.",
        ],
      },
    ],
    companions: [
      {
        songSlug: "tunnel-view",
        title: "Tunnel View",
        reason: "What happens when attention narrows too far and focus quietly becomes blindness.",
      },
      {
        songSlug: "the-pen-breathes",
        title: "The Pen Breathes",
        reason: "Where the gathered mind goes to work — language as an instrument rather than decoration.",
      },
    ],
  },

  "the-pen-breathes": {
    seoTitle: "The Pen Breathes — Buddha KA | The Diamond Sutra",
    seoDescription:
      "Buddha KA treats writing as a living discipline: language as an instrument of consciousness, authorship as responsibility, and the difference between decorated lines and words that actually carry life.",
    standfirst:
      "A study of writing as a living act — and of the line between decoration and transmission.",
    sections: [
      {
        eyebrow: "About the song",
        title: "About the Song",
        paragraphs: [
          "“The Pen Breathes” treats writing as something alive rather than something produced. The title makes a claim before a word is heard: the instrument is not inert. What passes through it is respiration — intake, pressure, release.",
          "That framing changes the stakes of a verse. If the pen breathes, then dead writing is not merely weak writing; it is writing with nothing moving through it. Craft can be present and life absent, and the two are easy to confuse from the outside.",
          "The song sits early in the album because language is the tool the rest of the record uses. Before perception, desire, and value can be interrogated, the instrument doing the interrogating has to be examined.",
        ],
      },
      {
        eyebrow: "The method",
        title: "Mirror Writing",
        paragraphs: [
          "Mirror Writing is the standard Buddha KA applies to language: every line earns its place, or it goes. Not every line has to be profound, but every line has to be doing work — carrying weight, turning a thought, holding a rhythm the meaning depends on.",
          "The discipline is subtractive. Most writing fails not because it lacks ideas but because it protects lines the writer is fond of. Mirror Writing removes the fondness from the decision and asks a colder question: what does this sentence do that no other sentence in the piece already does?",
          "Applied honestly, the method changes the writer as much as the work. A person who deletes their favourite line for the sake of the whole has practised something larger than editing.",
        ],
      },
      {
        eyebrow: "The idea",
        title: "Language as an Instrument of Consciousness",
        paragraphs: [
          "Words do not only describe interior states; they build them. The vocabulary a person has access to determines which experiences they can hold clearly enough to work with. What cannot be named tends to be lived blindly.",
          "This is why writing functions as a spiritual technology rather than an artistic hobby. To write precisely about an internal condition is to gain leverage over it. The imprecise version keeps running the person; the precise version can finally be addressed.",
          "The song's implicit argument is that authorship is a form of responsibility. Anyone shaping language is shaping the categories other people will think inside.",
        ],
      },
      {
        eyebrow: "Context",
        title: "Why It Matters in the Album",
        paragraphs: [
          "The Diamond Sutra repeatedly asks who wrote the material running inside a listener's head — the assumptions, the appetites, the frames. “The Pen Breathes” is the reverse angle: what it means to be the one writing.",
          "It pairs naturally with the album's language chapter later in the sequence, where speech is examined as a causal force rather than a report on reality. One track concerns the written instrument, the other the spoken one.",
          "Together they form the record's argument that consciousness and expression are not separate departments. What a person can say cleanly, they can see cleanly.",
        ],
      },
      {
        eyebrow: "Listening",
        title: "What to Listen For",
        paragraphs: [
          "Listen for economy. The interest of a song built on this premise is in what has been removed, not in what has been added.",
          "Listen for the difference between phrasing that impresses and phrasing that lands. Impressive writing asks to be admired; living writing asks to be used.",
          "Then read your own writing afterward — messages, notes, anything. The song's real effect is diagnostic.",
        ],
      },
    ],
    companions: [
      {
        songSlug: "speak-life",
        title: "Speak Life",
        reason: "The spoken counterpart — language as participation in what becomes real.",
      },
      {
        songSlug: "letter-from-the-higher-self",
        title: "Letter From the Higher Self",
        reason: "Writing turned inward, addressed from a clearer vantage than the present one.",
      },
    ],
  },

  "letter-from-the-higher-self": {
    seoTitle: "Letter From the Higher Self — Buddha KA | The Diamond Sutra",
    seoDescription:
      "Counsel written from a clearer internal vantage. Buddha KA explores perspective across levels of self, accountability without cruelty, and what the wiser frame would say if it could reach the present one.",
    standfirst:
      "Correspondence between the person you are and the vantage point that can already see further.",
    sections: [
      {
        eyebrow: "About the song",
        title: "About the Song",
        paragraphs: [
          "“Letter From the Higher Self” takes an unusual formal position: the song is addressed to the listener from a level of self that is not currently in charge. It is written in the register of counsel rather than command.",
          "The device is precise for a reason. Most self-talk is either flattery or prosecution. A letter has a different temperature — considered, unhurried, aware that it will be read later by someone in a different state than the one it was written for.",
          "Nothing here requires belief in a separate entity. The higher self in this framing is the vantage a person occasionally reaches and then loses: the clear hour, the honest afternoon, the moment the pattern was obvious.",
        ],
      },
      {
        eyebrow: "The idea",
        title: "Perspective Across Levels of Self",
        paragraphs: [
          "A person is not one continuous voice. There is the self that decides at midnight and the self that pays for it at six. There is the self that knows and the self that acts, and they are rarely in the room together.",
          "The value of the letter form is that it transfers clarity across that gap. What the clear self understands can be recorded and delivered to the version that will need it most and be least able to generate it.",
          "This is why the song reads as remembrance rather than instruction. The higher vantage is not adding new information. It is returning something the present self already knew and misplaced under pressure.",
        ],
      },
      {
        eyebrow: "The mechanism",
        title: "Accountability Without Cruelty",
        paragraphs: [
          "Harsh self-talk is often mistaken for discipline. It rarely produces change, because a mind under attack spends its energy defending rather than correcting.",
          "The counsel this song models is exacting but not punitive. It names the avoidance plainly, refuses the flattering explanation, and still addresses the person as someone worth speaking to seriously. That combination is rare and it is the point.",
          "Held that way, accountability becomes usable. The letter does not ask for shame; it asks for a decision.",
        ],
      },
      {
        eyebrow: "Context",
        title: "Inside The Diamond Sutra",
        paragraphs: [
          "Placed early in the album, the track establishes an internal witness before the record begins dismantling inherited perception and inherited desire. Something has to be doing the examining.",
          "That witness is what makes the later chapters survivable. Interrogating your own conditioning without a stable vantage produces despair; with one it produces authorship.",
          "It also connects to the album's writing thread. This is the pen turned inward — language used not to be heard by others, but to reach a future version of the writer.",
        ],
      },
      {
        eyebrow: "Listening",
        title: "What to Listen For",
        paragraphs: [
          "Listen for address. The song is speaking to someone, and the useful posture is to accept the letter as received rather than observed.",
          "Notice which passages produce resistance. Resistance is the marker of accuracy far more often than agreement is.",
          "Afterward, write the reply. The form invites it, and the answer a person gives is more revealing than anything the track can supply.",
        ],
      },
    ],
    companions: [
      {
        songSlug: "the-pen-breathes",
        title: "The Pen Breathes",
        reason: "The discipline behind the letter — writing as a living instrument.",
      },
      {
        songSlug: "spiritual-mechanics",
        title: "Spiritual Mechanics",
        reason: "How counsel becomes structure once it is treated as a working system.",
      },
    ],
  },

  "break-the-frame": {
    seoTitle: "Break the Frame — Buddha KA | The Diamond Sutra",
    seoDescription:
      "Manufactured reality, manufactured consent, manufactured identity. Buddha KA examines the systems operating beneath appearances and argues for proof over first impressions.",
    standfirst:
      "Every image arrives pre-cropped. The question is who chose the edges — and what was left outside them.",
    sections: [
      {
        eyebrow: "About the song",
        title: "About the Song",
        paragraphs: [
          "“Break the Frame” is the album's confrontation with construction. A frame is not a lie; it is a selection. It decides what counts as the picture and quietly disqualifies everything else from consideration.",
          "The song works across three layers of that construction: manufactured reality — the version of events presented as the whole; manufactured consent — agreement produced by repetition rather than reasoning; and manufactured identity — the self a person performs because the frame supplied the role.",
          "None of this is treated as conspiracy. It is treated as infrastructure. Framing is how any society transmits itself, and the failure is not that frames exist but that they are inherited unexamined.",
        ],
      },
      {
        eyebrow: "The mechanism",
        title: "How a Frame Does Its Work",
        paragraphs: [
          "A frame operates by pre-loading the question. By the time a person is asked what they think, the terms have been set, the options have been narrowed, and disagreement has been assigned a shape in advance.",
          "This is why arguing inside a frame rarely changes anything. The debate is permitted precisely because it is contained. Real movement begins one level up, at the question of who defined the terms and what they gain from those terms holding.",
          "The song's imperative is structural rather than rebellious. Breaking the frame is not contrarianism — reflexive opposition is just the frame operating in reverse. It is the refusal to accept a boundary as reality simply because it arrived first.",
        ],
      },
      {
        eyebrow: "The standard",
        title: "Proof Over First Impressions",
        paragraphs: [
          "A first impression is a fast frame. It compresses limited evidence into a confident conclusion and then defends that conclusion with everything encountered afterward.",
          "The alternative the record proposes is slower and more expensive: hold the impression as a hypothesis, keep looking, and let evidence rather than momentum decide. Applied to people, institutions, and to one's own self-image, that discipline is quietly radical.",
          "It also cuts inward. The most protected frame anyone carries is the one describing who they are, and it is usually the least examined.",
        ],
      },
      {
        eyebrow: "Context",
        title: "Inside The Diamond Sutra",
        paragraphs: [
          "This track opens the album's central sequence on conditioning. What follows examines inherited seeing, inherited thinking, and inherited wanting — three specific frames operating at close range.",
          "“Break the Frame” supplies the general principle those chapters apply. It establishes that the object of the record's attention is never the content of a belief but the architecture holding it.",
          "The diamond running through the album's title is relevant here. A blade that cuts clean does not argue with what it removes.",
        ],
      },
      {
        eyebrow: "Listening",
        title: "What to Listen For",
        paragraphs: [
          "Listen for the shift from object to structure — the move from what is being shown to what is doing the showing.",
          "Notice how quickly the mind wants a villain. Frames rarely have one, which is exactly why they survive.",
          "Then apply it locally. The most useful frame to break is not a distant institutional one but the small daily story a person has stopped questioning.",
        ],
      },
    ],
    companions: [
      {
        songSlug: "borrowed-eyes",
        title: "Borrowed Eyes",
        reason: "The same inquiry at the level of perception — whose lens is doing the seeing.",
      },
      {
        songSlug: "tunnel-view",
        title: "Tunnel View",
        reason: "What a frame costs once the field of awareness narrows around it.",
      },
    ],
  },

  "borrowed-eyes": {
    seoTitle: "Borrowed Eyes — Buddha KA | The Diamond Sutra",
    seoDescription:
      "Inherited perception under examination. Buddha KA traces how culture, authority, and comparison install a lens before a person ever chooses one — and separates perception from reality.",
    standfirst:
      "Sight feels immediate. It almost never is.",
    sections: [
      {
        eyebrow: "About the song",
        title: "About the Song",
        paragraphs: [
          "“Borrowed Eyes” examines the most invisible inheritance of all: the lens. Beliefs can be argued with because they announce themselves as beliefs. Perception does not announce anything. It presents itself as the world.",
          "The song's proposition is that most seeing is second-hand. Before a person forms an opinion about what is in front of them, a great deal has already happened — categories assigned, significance ranked, threat and value pre-sorted by training that predates memory.",
          "That training arrives from ordinary sources: family, culture, authority, comparison, the particular era a person happened to be born into. None of it feels like influence from the inside. It feels like clarity.",
        ],
      },
      {
        eyebrow: "The distinction",
        title: "Perception Is Not Reality",
        paragraphs: [
          "The phrase is common; the implication is rarely absorbed. If perception is constructed, then confidence is not evidence, and the felt certainty accompanying a first look is a property of the viewer rather than the viewed.",
          "The song holds that line without collapsing into relativism. It is not claiming nothing is real. It is claiming the seeing apparatus has a history, and that history has interests.",
          "Comparison is singled out as one of the strongest lenses. To see through comparison is to see everything as a ranking, which quietly deletes the thing itself in favour of its position.",
        ],
      },
      {
        eyebrow: "The method",
        title: "Returning the Lens",
        paragraphs: [
          "Mirror Thinking asks what is behind a thought rather than answering the thought. Applied to perception the sequence is concrete: describe what is actually present without interpretation, then notice which words arrived carrying judgment, then ask who taught the mind to weight them that way.",
          "What survives that process is closer to observation. What dissolves was borrowed, and it was being worn as vision.",
          "The aim is not to see through no lens — that is not available to anyone. It is to know the prescription you are wearing, which is the difference between wearing glasses and being glasses.",
        ],
      },
      {
        eyebrow: "Context",
        title: "Inside The Diamond Sutra",
        paragraphs: [
          "This is the first of the album's paired inheritance studies. Where the following track examines inherited thought and the one after examines inherited desire, this one examines inherited sight — the layer that runs before either.",
          "The order matters. Thinking and wanting both operate on material that perception has already selected and labelled. Correcting the later stages while leaving the lens untouched changes very little.",
          "Read together, the three tracks form the record's core argument: the interior is largely furnished by other people, and authorship begins with an inventory.",
        ],
      },
      {
        eyebrow: "Listening",
        title: "What to Listen For",
        paragraphs: [
          "Listen for the moment the song stops describing the world and starts describing the viewer. The pivot is the thesis.",
          "Notice any impulse to apply this to other people first. That impulse is the lens defending itself.",
          "Afterward, take one strong recent reaction and trace it back to whoever taught you to read that signal. The trail is usually short.",
        ],
      },
    ],
    companions: [
      {
        songSlug: "borrowed-minds",
        title: "Borrowed Minds",
        reason: "The companion study — inherited thought wearing the costume of original thinking.",
      },
      {
        songSlug: "break-the-frame",
        title: "Break the Frame",
        reason: "The structural version of the same problem: who decided where the picture ends.",
      },
      {
        songSlug: "who-taught-you-to-want",
        title: "Who Taught You to Want?",
        reason: "The desire chapter — the same question aimed at appetite instead of sight.",
      },
    ],
  },

  "borrowed-minds": {
    seoTitle: "Borrowed Minds — Buddha KA | The Diamond Sutra",
    seoDescription:
      "Repetition is not revelation. Buddha KA examines acquired mental structures that pass for original thought, and what remains of a position once its sources are named.",
    standfirst:
      "Most thinking is transmission. The work is finding out what, if anything, originated with you.",
    sections: [
      {
        eyebrow: "About the song",
        title: "About the Song",
        paragraphs: [
          "“Borrowed Minds” makes a claim most people would rather not test: that a substantial portion of what feels like personal thought is inherited material running at speed.",
          "The song is not dismissive about this. Inheritance is how knowledge moves. A person who refused every idea they did not personally generate would end up primitive, not free. The problem is not borrowing — it is borrowing without a record of the loan.",
          "What the track objects to is the counterfeit: acquired structure presented, sincerely, as original insight. The counterfeit is convincing because the borrower is not lying. They genuinely cannot see the seam.",
        ],
      },
      {
        eyebrow: "The principle",
        title: "Repetition Is Not Revelation",
        paragraphs: [
          "Repetition produces the feeling of truth. A statement heard often enough acquires the texture of something known, and the nervous system stops distinguishing familiarity from verification.",
          "This is why conviction is such a poor instrument for measuring accuracy. Strength of belief tracks exposure, not evidence. The loudest certainties in a person's head are frequently the ones they were exposed to earliest and questioned least.",
          "The song's test is simple and uncomfortable: can the position be argued from first principles, or only recited? Recitation survives agreement and collapses under pressure.",
        ],
      },
      {
        eyebrow: "The mechanism",
        title: "Acquired Structure",
        paragraphs: [
          "Borrowed material rarely arrives as a single opinion. It arrives as architecture — which questions are worth asking, what counts as evidence, which outcomes are respectable, who is permitted to be right.",
          "Because the structure is invisible, disagreements between two borrowed minds can be intense while remaining entirely contained. Both parties argue furiously inside a room neither of them built.",
          "Mirror Thinking is the exit. Rather than answering the thought, it turns the thought around: where did this come from, whose interest does it serve, and what would remain if the source were removed?",
        ],
      },
      {
        eyebrow: "Context",
        title: "Its Place on the Album",
        paragraphs: [
          "This track is the direct companion to “Who Taught You to Want?”. One examines inherited cognition, the other inherited appetite, and they share a method and a temperature.",
          "It also completes the movement begun by “Borrowed Eyes”. Sight, thought, desire: the three layers through which another person's world becomes an individual's interior without anyone noticing the transfer.",
          "Placed at this point in the sequence, it functions as the album's turn from diagnosis toward authorship. Everything after it is concerned with what a person builds once the inventory is honest.",
        ],
      },
      {
        eyebrow: "Listening",
        title: "What to Listen For",
        paragraphs: [
          "Listen for attribution. The song is interested in sources, and its energy comes from naming rather than condemning them.",
          "Notice the difference between ideas you can extend and ideas you can only repeat. Extension is the signature of ownership.",
          "Then keep the ones that survive. Borrowed thought that has been examined and consciously retained is no longer borrowed — it has been bought.",
        ],
      },
    ],
    companion: {
      songSlug: "who-taught-you-to-want",
      title: "Who Taught You to Want?",
      reason: "The desire chapter — the same interrogation aimed at appetite.",
    },
    companions: [
      {
        songSlug: "borrowed-eyes",
        title: "Borrowed Eyes",
        reason: "The layer beneath thought — inherited perception doing the selecting.",
      },
    ],
  },
};
